import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import { pageJustPublishedSignal } from '../utils/pageJustPublishedSignal.ts';

import { matchesMeetingNotesSignal } from './matchesMeetingNotesSignal.ts';

/**
 * OnPublish event-trigger detection for the Meeting Notes nudge.
 *
 * Restricted to the view page: the signal is armed in the editor publish flow, so it must only
 * surface once the user lands on the published page's view surface — never back in edit/live mode.
 *
 * The `confluence-automation-meeting-notes` exposure fires at publish in `usePublish` for every
 * candidate — hence the no-exposure check here.
 */
export const useMeetingNotesNudgeOnPublishSignal = (contentId: string): boolean => {
	const { edition, isRovoLLMEnabled, isAdminHubAIEnabled } = useSessionData();
	const pageMode = useGetPageMode();
	const hasDismissedNudge = confluenceLocalStorageInstance.getItemAsBoolean(
		keys.AUTOMATION_DISCOVER_MEETING_NOTES_TEMPLATE,
	);

	const hasPageJustPublishedSignalForThisContent =
		Boolean(contentId) && pageJustPublishedSignal.contentId === contentId;

	return Boolean(
		hasPageJustPublishedSignalForThisContent &&
		pageMode === PageMode.VIEW &&
		!hasDismissedNudge &&
		isRovoLLMEnabled &&
		isAdminHubAIEnabled &&
		(edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM) &&
		expValEquals('confluence-automation-meeting-notes', 'isEnabled', true) &&
		matchesMeetingNotesSignal({
			title: pageJustPublishedSignal.title,
			sourceTemplateEntityId: pageJustPublishedSignal.sourceTemplateEntityId ?? '',
		}).matches,
	);
};
